// The admin tools, as ONE list — what the admin tab strip shows, in what order,
// and who may see each tab. Both shells draw their strip from this.
//
// ⚠️ THIS IS NOT THE GATE. Hiding a tab hides a tab; every one of these routes
// is still gated at the route in MobileRoutes / DesktopRoutes AND inside its own
// screen (see the note on AdminOnly in MobileRoutes). This list only decides
// what is worth drawing.
//
// `perm` names the AppContext flag that grants the tool:
//   'superAdmin' → isSuperAdmin
//   'tuya'       → canTuyaTest — AllowTestingTuya, which the bench operators
//                  hold WITHOUT being Super Admins. Such a user sees one tab.
// `nativeOnly` → only where the radio exists (BLE Test, mobile shell only).

export const ADMIN_TOOLS = [
  { path: '/mqtt', label: 'Message Box', perm: 'superAdmin' },
  { path: '/device-tracking', label: 'Device Tracking', perm: 'superAdmin' },
  { path: '/tuya-testing', label: 'Tuya Testing', perm: 'tuya' },
  { path: '/firmware', label: 'Firmware', perm: 'superAdmin' },
  { path: '/device-admin', label: 'Device Admin', perm: 'superAdmin' },
  { path: '/user-access', label: 'User Access', perm: 'superAdmin' },
  { path: '/installed', label: 'Installed', perm: 'superAdmin' },
  { path: '/device-types', label: 'Device Types', perm: 'superAdmin' },
  { path: '/companies', label: 'Companies', perm: 'superAdmin' },
  { path: '/rf-log', label: 'RF Log', perm: 'superAdmin' },
  { path: '/rf-signatures', label: 'RF Signatures', perm: 'superAdmin' },
  { path: '/ble-test', label: 'BLE Test', perm: 'superAdmin', mobileOnly: true },
];

/** May this user see this tool? */
export function toolAllowed(tool, { isSuperAdmin, canTuyaTest } = {}) {
  if (!tool) return false;
  if (tool.perm === 'tuya') return !!canTuyaTest;
  return !!isSuperAdmin;
}

/**
 * The tools this user gets in this shell, in strip order.
 * `shell` is 'mobile' or 'desktop'.
 */
export function visibleTools(perms, shell) {
  return ADMIN_TOOLS.filter((t) => (
    toolAllowed(t, perms) && !(t.mobileOnly && shell !== 'mobile')
  ));
}

/** The tool a pathname belongs to, or null. Exact path, trailing slash allowed. */
export function toolForPath(pathname) {
  const p = String(pathname || '').replace(/\/+$/, '') || '/';
  return ADMIN_TOOLS.find((t) => t.path === p) || null;
}

// ── "Admin Tools" opens the one you used last ──────────────────────────────
// Per-viewer convenience only, so localStorage — and it can throw or come back
// empty (private window, cleared data), which simply means "the first tab".
const LAST_KEY = 'sli.adminTools.last';

export function rememberTool(path) {
  try { localStorage.setItem(LAST_KEY, path); } catch (e) { /* ignore */ }
}

/** Where the "Admin Tools" entry should land for this user, or null for none. */
export function adminToolsEntry(perms, shell) {
  const tools = visibleTools(perms, shell);
  if (!tools.length) return null;
  let last = null;
  try { last = localStorage.getItem(LAST_KEY); } catch (e) { /* ignore */ }
  // A remembered tool this user may no longer open (permission removed, or a
  // mobile-only tool remembered from the phone) falls back to the first tab.
  const hit = tools.find((t) => t.path === last);
  return (hit || tools[0]).path;
}
